import React, { FC, memo, ReactNode, useRef, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { DevTool } from '@hookform/devtools'
import { pick } from 'lodash-es'
import { FormProvider, useForm } from 'react-hook-form'

import { SIGN_UP_CUSTOMER, LOGOUT_CUSTOMER, GET_CURRENT_SHOP_TWO_FACTOR_AUTH } from 'Const'
import { SignUpStates, useSignUpState } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import { Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

import Code from './Code'
import Identify from './Identify'
import LoggedCustomerView from './LoggedCustomerView'
import Phone from './Phone'
import ResendButton from './ResendButton'
import VerifiedBadge from './VerifiedBadge'

export interface FormState {
  code?: string
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
}

export type SignedUpCustomer = Pick<Customer, 'firstName' | 'lastName' | 'email' | 'phone'>

export interface CustomerFieldsGroupProps {
  customer?: Customer | null
  isActive?: boolean
  onChangeStep?: () => void
  onComplete?: (customer: SignedUpCustomer) => void
  renderSubmitButton?: (submit: () => void, submitting: boolean, isPhoneStep: boolean) => ReactNode
  submitting?: boolean
}

const RESEND_COOLDOWN_SECONDS = 60
const DEFAULT_FORM_VALUES: FormState = {
  code: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  phone: '',
  subscribed: true,
}

const CustomerFieldsGroup: FC<CustomerFieldsGroupProps> = ({
  isActive = true,
  onChangeStep,
  onComplete = () => {},
  renderSubmitButton,
  submitting = false,
  customer,
}) => {
  const { data: currentShopData } = useQuery(GET_CURRENT_SHOP_TWO_FACTOR_AUTH)
  const currentShop = currentShopData?.currentShop
  const [state, setState] = useSignUpState(customer?.signedUp ? SignUpStates.COMPLETED : SignUpStates.PHONE)

  const [showDetails, setShowDetails] = useState(false)

  if (!showDetails && (state.isForm || state.isMultiple)) {
    setShowDetails(true)
  }

  const form = useForm<FormState>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)
  const wasCodeStepRef = useRef(false)
  const codeInputRef = useRef<HTMLInputElement | null>(null)

  const { clearErrors, handleSubmit, reset, getValues, setValue } = form

  const [signUp, { loading: signing }] = useMutation(SIGN_UP_CUSTOMER, {
    onCompleted: (data) => {
      if (data.signUpCustomer.state == SignUpStates.COMPLETED) {
        const customer = data.signUpCustomer.customer
        onComplete(pick(customer, ['firstName', 'lastName', 'email', 'phone']))
        sendGTMAccountCreatedEvent(customer as Customer)
      } else if (data.signUpCustomer.state == SignUpStates.CODE) {
        noty('Enter code from SMS', 'success')

        if (!wasCodeStepRef.current) {
          wasCodeStepRef.current = true
          startResendCooldown()
          window.requestAnimationFrame(() => {
            codeInputRef.current?.focus()
          })
        }
      }
      setState(data.signUpCustomer.state as SignUpStates)
    },
  })
  const [logout] = useMutation(LOGOUT_CUSTOMER)

  const handleLogout = useEvent(
    () =>
      void logout().then(() => {
        onChangeStep?.()
        setState(SignUpStates.PHONE)
        setResendSecondsLeft(0)
        setShowDetails(false)
        reset(DEFAULT_FORM_VALUES)
        wasCodeStepRef.current = false
      }),
    [logout, onChangeStep],
  )
  const startResendCooldown = useEvent(() => setResendSecondsLeft(RESEND_COOLDOWN_SECONDS), [])
  const stopResendCooldown = useEvent(() => setResendSecondsLeft(0), [])

  const onSubmit = handleSubmit((input: FormState) => {
    if (customer?.signedUp) {
      onComplete(pick(customer, ['firstName', 'lastName', 'email', 'phone']))
    } else {
      void signUp({ variables: { input } })
    }
  })

  const handleResend = useEvent(() => {
    if (resendSecondsLeft > 0) return
    startResendCooldown()
    clearErrors('code')
    setValue('code', '')
    codeInputRef.current?.focus()
    void signUp({ variables: { input: getValues() } })
  })

  const phoneSectionDescription = state.isPhone
    ? 'We use this number to send a verification code for checkout'
    : state.isCode
      ? 'We have sent a 6-digit authorization code to your number'
      : 'Phone number is successfully verified'

  return (
    <FormProvider {...form}>
      <DevTool control={form.control} />
      <div className="checkout-step / mb-5">
        <h2 className="checkout-step__header / h4">
          <span>Contact</span>

          {isActive ? (
            state.isPhone ? null : (
              <button className="checkout-button / ml-auto" type="button" onClick={handleLogout}>
                Log out
              </button>
            )
          ) : onChangeStep ? (
            <button className="checkout-button / ml-auto" type="button" onClick={onChangeStep}>
              Change
            </button>
          ) : null}
        </h2>

        <form
          className="checkout-contact-form"
          hidden={!isActive || customer?.signedUp}
          onSubmit={(e) => void onSubmit(e)}
        >
          {state.isCode ? (
            <p aria-live="polite" className="sr-only" role="status">
              Enter the 6-digit verification code to continue checkout.
            </p>
          ) : null}

          <div className="checkout-contact-flow">
            {customer?.signedUp ? null : (
              <section className="checkout-contact-flow__panel checkout-contact-flow__panel--phone">
                {currentShop?.twoFactorAuth ? (
                  <>
                    <div className="checkout-contact-flow__title-row">
                      <h3 className="checkout-contact-flow__title">Verify Phone</h3>
                      {state.isCode || state.isPhone ? null : <VerifiedBadge />}
                    </div>
                    <p className="checkout-contact-flow__description mb-1">{phoneSectionDescription}</p>
                  </>
                ) : null}
                <Phone disabled={!state.isPhone} submitting={signing || submitting} />
                <div aria-hidden={!state.isCode} className="checkout-contact-flow__code-reveal" hidden={!state.isCode}>
                  {state.isCode ? (
                    <Code ref={codeInputRef} canAutoSubmit={!(signing || submitting)} onSubmit={() => void onSubmit()}>
                      <ResendButton duration={resendSecondsLeft} onResend={handleResend} onStop={stopResendCooldown} />
                    </Code>
                  ) : null}
                </div>
              </section>
            )}

            {showDetails || state.isPassword ? <Identify showDetails={showDetails} state={state} /> : null}
          </div>
        </form>
        {customer?.signedUp ? (
          <LoggedCustomerView email={customer.email} firstName={customer.firstName} lastName={customer.lastName} />
        ) : null}
      </div>

      {isActive && renderSubmitButton
        ? renderSubmitButton(() => void onSubmit(), signing || submitting, state.isPhone)
        : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
